import React, { useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';

interface NextLocation {
  pathname: string;
  search: string;
  hash?: string;
  state?: unknown;
}

interface QueryLocation {
  href: string;
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
}

/**
 * use-query-params v1 speaks the React Router 5 history API.
 * This adapter forwards those updates through React Router 6.
 *
 * LocationProvider writes query changes from `history.location` when it
 * exists, and otherwise from a ref it only refreshes in useEffect. Child
 * effects (the profiles filter bar writing default profileTypes) run first,
 * so a missing `history.location` keeps the previous pathname and the
 * replace lands on /:org?profileTypes=... instead of /:org/profiles.
 */
const QueryParamAdapter = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const locationRef = useRef<QueryLocation | null>(null);

  const location = useMemo<QueryLocation>(
    () => ({
      href: window.location.href,
      pathname: routerLocation.pathname,
      search: routerLocation.search,
      hash: routerLocation.hash,
      state: routerLocation.state,
      key: routerLocation.key,
    }),
    [routerLocation]
  );
  locationRef.current = location;

  const history = useMemo(
    () => ({
      get location() {
        return locationRef.current;
      },
      push(next: NextLocation) {
        navigate(
          {
            pathname: next.pathname,
            search: next.search,
            hash: next.hash,
          },
          { state: next.state }
        );
      },
      replace(next: NextLocation) {
        navigate(
          {
            pathname: next.pathname,
            search: next.search,
            hash: next.hash,
          },
          { replace: true, state: next.state }
        );
      },
    }),
    [navigate]
  );

  return (
    <QueryParamProvider history={history as any} location={location as any}>
      {children}
    </QueryParamProvider>
  );
};

export default QueryParamAdapter;
